<script setup>
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Select from "primevue/select";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Checkbox from "primevue/checkbox";
import Slider from "primevue/slider";
import {nextTick, onMounted, ref} from "vue";
import { trans } from 'laravel-vue-i18n';
import {useForm} from "laravel-precognition-vue-inertia";
import html2canvas from "html2canvas";
import {usePage} from "@inertiajs/vue3";
import {useToast} from "primevue/usetoast";

const showFeedbackForm = ref(false);
const imageData = ref(null);
const showFeedback = () => {
    nextTick().then(() => {
        showFeedbackForm.value = true;
    })
    //html2canvas(document.body).then((canvas) => {
    //    imageData.value = canvas.toDataURL();
    //});
}

const feedbackTypes = [
    { type: 'feedback', name: trans('feedback.type_bug') },
    { type: 'feature', name: trans('feedback.type_feature') },
];

const featureConnections = [
    { type: 'design', name: trans('feedback.conn_design') },
    { type: 'mobile', name: trans('feedback.conn_mobile') },
    { type: 'reservations', name: trans('feedback.conn_reservations') },
    { type: 'integrations', name: trans('feedback.conn_integrations') },
    { type: 'settings', name: trans('feedback.conn_settings') },
    { type: 'theming', name: trans('feedback.conn_theming') },
];

const errors = ref({});
const loading = ref(false);
const form = ref({
    feedbackType: "feature",
    feature_connection: "",
    page: window.location,
    urgency: 0,
    body1: "",
    body2: "",
    jsDump: "",
    screenshot: "",
    user: usePage().props.user.maeum_id,
    app: usePage().props.appid
});

const toast = useToast();
const submit = async () => {
    loading.value = true;
    errors.value = {};
    if (doSendScreenshot.value) {
        form.value.screenshot = imageData.value;
    }
    try {
        await axios.post(`${usePage().props.olympus.url}/api/feedback`, form.value);
        form.value = {
            feedbackType: "feature",
            feature_connection: "",
            page: window.location,
            urgency: 0,
            body1: "",
            body2: "",
            jsDump: "",
            screenshot: "",
            user: usePage().props.user.maeum_id,
            app: usePage().props.appid
        };
        showFeedbackForm.value = false;
        toast.add({
            severity: 'success',
            summary: 'Feedback',
            detail: trans('feedback.sent'),
            life: 2000,
        });
    } catch (error) {
        if (error.response && error.response.data.errors) {
            errors.value = error.response.data.errors;
        }
    }
    loading.value = false;
};

const urgency = [
    { name: trans('feedback.urgency_0_name'), description: trans('feedback.urgency_0_desc') },
    { name: trans('feedback.urgency_1_name'), description: trans('feedback.urgency_1_desc') },
    { name: trans('feedback.urgency_2_name'), description: trans('feedback.urgency_2_desc') },
    { name: trans('feedback.urgency_3_name'), description: trans('feedback.urgency_3_desc') },
    { name: trans('feedback.urgency_4_name'), description: trans('feedback.urgency_4_desc') },
    { name: trans('feedback.urgency_5_name'), description: trans('feedback.urgency_5_desc') },
];

const doSendScreenshot = ref(false);
</script>

<template>
    <div class="pointer-events-auto">
        <Button @click="showFeedback" rounded variant="text" size="large" severity="secondary">
            <template #icon>
                <i class="pi pi-comments"></i>
            </template>
        </Button>
    </div>

    <Dialog v-model:visible="showFeedbackForm" modal class="md:min-w-[600px] select-none">
        <template #container>
            <div class="absolute -top-4 -right-4 z-50">
                <Button icon="pi pi-times" @click="showFeedbackForm = false" rounded severity="secondary"></Button>
            </div>
            <div class="flex flex-col overflow-hidden rounded-xl relative">
                <div class="bg-roori-home p-5 border-b dark:border-neutral-700">
                    <i class="pi pi-comments !text-2xl mb-2"></i>
                    <div>
                        <p class="text-lg font-bold text-gray-800 dark:text-gray-200">{{ $t('feedback.title') }}</p>
                        <p class="text-sm text-gray-600 dark:text-gray-300">{{ $t('feedback.subtitle') }}</p>
                    </div>
                </div>
                <div class="p-5 bg-neutral-50 dark:bg-neutral-800 dark:border-neutral-700 border-b">
                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.what_send') }}</label>
                        <Select v-model="form.feedbackType" :options="feedbackTypes" option-label="name" option-value="type"></Select>
                    </div>
                </div>

                <div v-if="form.feedbackType === 'feature'" class="p-5 flex flex-col gap-3 border-b dark:border-neutral-700">
                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.feature_area') }}</label>
                        <Select v-model="form.feature_connection" :options="featureConnections" :placeholder="$t('feedback.please_select')" option-label="name" option-value="type"></Select>
                        <small v-if="errors.feature_connection" class="text-red-500">{{ errors.feature_connection[0] }}</small>
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.feature_describe') }}</label>
                        <Textarea v-model="form.body1"/>
                        <small v-if="errors.body1" class="text-red-500">{{ errors.body1[0] }}</small>

                    </div>

                    <div class="text-center text-xs opacity-50 -mb-2">{{ $t('feedback.powered_by') }}</div>
                </div>

                <div v-else class="p-5 flex flex-col gap-3 border-b dark:border-neutral-700">
                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.bug_area') }}</label>
                        <Select v-model="form.feature_connection" :options="featureConnections" :placeholder="$t('feedback.please_select')" option-label="name" option-value="type"></Select>
                        <small v-if="errors.feature_connection" class="text-red-500">{{ errors.feature_connection[0] }}</small>

                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.bug_describe') }}</label>
                        <Textarea v-model="form.body1"/>
                        <small v-if="errors.body1" class="text-red-500">{{ errors.body1[0] }}</small>
                    </div>


                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.expected_behavior') }}</label>
                        <Textarea v-model="form.body2"/>
                        <small v-if="errors.body1" class="text-red-500">{{ errors.body1[0] }}</small>
                    </div>

                    <div class="flex flex-col mb-2">
                        <label class="text-sm mb-2">{{ $t('feedback.urgency_label') }}</label>
                        <div class="flex items-center p-4 rounded-lg gap-6 border dark:border-neutral-700 ">
                            <Slider v-model="form.urgency" :step="20" class="grow"></Slider>
                            <p class="w-2/6 text-center">
                                {{urgency[form.urgency / 20]?.name}}
                                <i class="pi pi-info-circle ml-1" v-tooltip="urgency[form.urgency / 20]?.description"></i>
                            </p>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <label class="text-sm mb-1">{{ $t('feedback.bug_page') }}</label>
                        <InputText v-model="form.page" disabled/>
                    </div>

                    <div class="text-center text-xs opacity-50 -mb-2">{{ $t('feedback.powered_by') }}</div>
                </div>

                <div class="bg-neutral-50 gap-2 dark:bg-neutral-800 p-5 flex justify-between">
                    <template v-if="false && form.feedbackType === 'feedback'">
                        <Checkbox v-model="doSendScreenshot" binary></Checkbox>
                        <p>{{ $t('feedback.attach_screenshot') }}</p>
                    </template>

                    <div class="grow"></div>
                    <Button :label="$t('feedback.submit_btn')" @click="submit" :loading="loading"></Button>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<style scoped lang="scss">

</style>
